/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { hasPermission } from '@/shared/utils/permissions';
import type { PermissionAction } from '@/shared/utils/permissions.types';
import { useAgencyPermissions } from '../hooks/use-agency-permissions';

interface RequireAgencyPermissionProps {
  /** The permission the current user must hold to see `children`. */
  permission: PermissionAction;
  /** Route to redirect to when the permission is missing. */
  redirectTo: string;
  children: ReactNode;
}

/**
 * Route guard that renders `children` only when the current user holds
 * `permission` in the active agency, redirecting otherwise.
 *
 * Roles are derived from `agency.members`, which arrives asynchronously. Until
 * it does, every role resolves to `anonymus`, so redirecting on that first
 * render would bounce even a legitimate owner. This renders nothing while the
 * member list is still empty and only decides once it is known.
 *
 * @returns The guarded children, a redirect, or null while membership loads.
 */
export function RequireAgencyPermission({
  permission,
  redirectTo,
  children,
}: RequireAgencyPermissionProps) {
  const { agency } = useAgency((state) => state);
  const { currentUserRole } = useAgencyPermissions();

  if (!agency?.members?.length) return null;
  if (!hasPermission(permission, currentUserRole)) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}
